/* eslint-disable react/jsx-one-expression-per-line */
import { ReactElement, useContext, useState, useEffect } from 'react';
import { Field } from 'formik';
import gridStyle from '@pages/forms/Grid.module.scss';
import { Alert, Button, Modal, TextInput, Accordion } from '@components';
import VicFinesContext from '@pages/forms/VicFinesFormContext';
import InfoIcon from '../../../../images/info.svg';
import QuestionIcon from '../../../../images/question.svg';
import mailRef from '../../../../images/refNo_mail.jpg';
import handwrittenRef from '../../../../images/refNo-handwritten.jpg';
import planRef from '../../../../images/refNo-plan.jpg';
import styles from './FVSStep1.module.scss';

function FVSStep1(): ReactElement {
  const { apiError } = useContext(VicFinesContext);
  const FVSStep1AlertContent = (): ReactElement => <p>{`${apiError?.Error}`}</p>;
  const [isOpenHelp, setOpenHelp] = useState(false);
  const [smallScreen, setMatches] = useState(window.matchMedia('(max-width: 1150px)').matches);

  const closeNotifications = () => {
    setOpenHelp(false);
  };

  const FVSStep1NoteContent = (): ReactElement => (
    <>
      <p>You will need to enter the fine's obligation number or infringement notice number, the offence date and some other details about yourself or your vehicle.</p>
      <span>
        If you don't have these details please contact us on{' '}
        <a href="tel:0392008111" title="Fines Victoria phone number">
          03 9200 8111
        </a>{' '}
        (
        <a href="tel:1300369819" title="Fines Victoria phone number">
          1300 369 819
        </a>{' '}
        regional callers).
      </span>
    </>
  );

  const accordionContent = (caption: string, image: string, imageAlt: string): ReactElement => (
    <div className={styles.accordionContent}>
      <p className={styles.caption}>{caption}</p>
      <img className={styles.accordionImage} src={image} alt={imageAlt} />
    </div>
  );

  const notificationInformation = (): ReactElement => (
    <>
      {smallScreen ? (
        <>
          <Accordion title="Did you receive your fine by mail?" popupContent={accordionContent('Enter your obligation number', mailRef, 'mail reference number')} alwaysOpen />
          <Accordion
            title="Did a police officer give you the fine in person, or issue you with a handwritten fine?"
            popupContent={accordionContent('Enter your enforcement agency identifying reference', handwrittenRef, 'handwritten reference number')}
            alwaysOpen
          />
          <Accordion
            title="Are you on a payment plan or payment order?"
            popupContent={accordionContent('Enter your payment arrangement number', planRef, 'plan reference number')}
            alwaysOpen
          />
        </>
      ) : (
        <>
          <div className={styles.bitmapSection}>
            <span className={styles.bitmapTitle}>Did you receive your fine by mail?</span>
            <span className={styles.bitmapTitle}>Did a police officer give you the fine in person, or issue you with a handwritten fine?</span>
            <span className={styles.bitmapTitle}>Are you on a payment plan or payment order?</span>
            <div className={styles.bitmapImage}>
              <img className={styles.image} src={mailRef} alt="mail reference number" />
            </div>
            <div className={styles.bitmapImage}>
              <img className={styles.image} src={handwrittenRef} alt="handwritten reference number" />
            </div>
            <div className={styles.bitmapImage}>
              <img className={styles.image} src={planRef} alt="plan reference number" />
            </div>
            <div className={styles.sectionDescription}>
              <span>Enter your obligation number</span>
            </div>
            <div className={styles.sectionDescription}>
              <span>Enter your enforcement agency identifying reference</span>
            </div>
            <div className={styles.sectionDescription}>
              <span>Enter your payment arrangement number</span>
            </div>
          </div>
          <p className={styles.sectionTitle}>Don't have your fine on you?</p>
          <div className={styles.sectionDescription}>
            <span>Call Fines Victoria, Monday to Friday, 8am to 6pm (except public holidays).</span>
            <p>
              For infringement enquiries, call{' '}
              <a className={styles.contactLink} href="tel:0392008111" title="Fines Victoria phone number">
                03 9200 8111
              </a>{' '}
              or{' '}
              <a className={styles.contactLink} href="tel:1300369819" title="Fines Victoria phone number">
                1300 369 819
              </a>{' '}
              for regional callers.
            </p>
            <span>
              For Notice of Final Demand enquiries, call{' '}
              <a className={styles.contactLink} href="tel:0392008222" title="Fines Victoria phone number">
                03 9200 8222
              </a>{' '}
              or{' '}
              <a className={styles.contactLink} href="tel:1800150410" title="Fines Victoria phone number">
                1800 150 410
              </a>{' '}
              for regional callers.
            </span>
          </div>
          <div className={styles.btnFooter}>
            <Button onClick={closeNotifications} color="blue" size="small">
              Continue
            </Button>
          </div>
        </>
      )}
    </>
  );

  useEffect(() => {
    window.matchMedia('(max-width: 768px)').addEventListener('change', (e) => setMatches(e.matches));
  }, []);

  return (
    <>
      <div className={gridStyle.form}>
        <div className="vfH2">Your fine</div>
        <span className="subHeadingH3">
          At this stage of the application, you only need to provide the details of one of your fines - even if you’re dealing with multiple fines.
        </span>
        {apiError?.Error && <Alert icon={InfoIcon} message={<FVSStep1AlertContent />} theme="error" classAlt={styles.boxheight} />}
        <Field
          name="OBLIGATION_NUMBER"
          component={TextInput}
          title="Obligation number or infringement notice number"
          icon={QuestionIcon}
          iconPosition="end"
          iconClick={() => setOpenHelp(true)}
          isMandatory
          autocomplete="off"
        />
        {isOpenHelp && <Modal close={closeNotifications} show title="Not sure what to enter?" content={notificationInformation()} />}
      </div>
      <Alert message={<FVSStep1NoteContent />} theme="info" icon={InfoIcon} />
    </>
  );
}

export default FVSStep1;
